import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { ArrowLeft, Printer, Search, CheckSquare, Square, Settings2, CreditCard, FileText, QrCode } from 'lucide-react';
import { useEmployees } from '@/api/queries/employee.queries';
import { selectCompanyName } from '@/store/slices/companySlice';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

const ACCENT_PRESETS = ['#5B6CF0', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#0EA5E9'];

// Safely extract string from a field that may be an object {id, name} or a plain string
const strVal = (v) => (v && typeof v === 'object') ? (v.name || '') : (v || '');

const DEFAULT_SETTINGS = {
  accentColor: '#5B6CF0',
  // ID card
  showDesignation: true,
  showDept: true,
  showJoining: true,
  showEmpCode: true,
  // Personal info
  showIdentity: true,
  showEmployment: true,
  showContact: true,
  showBank: true,
  showConfidential: true,
};

function IdCard({ emp, companyName, settings }) {
  const { accentColor, showDesignation, showDept, showJoining, showEmpCode } = settings;
  return (
    <div style={{
      width: '85.6mm', height: '54mm', fontFamily: 'Arial, sans-serif',
      position: 'relative', background: '#fff', borderRadius: '4mm',
      overflow: 'hidden', border: '0.5px solid #ddd', boxSizing: 'border-box',
      pageBreakInside: 'avoid',
    }}>
      {/* Top band */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '8mm', background: accentColor, display: 'flex', alignItems: 'center', padding: '0 2.5mm' }}>
        <span style={{ color: '#fff', fontSize: '7px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
          {companyName}
        </span>
      </div>

      {/* Photo */}
      <div style={{ position: 'absolute', top: '10mm', left: '3mm' }}>
        {emp.photo_url ? (
          <img src={emp.photo_url} alt="" style={{ width: '14mm', height: '14mm', borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}/>
        ) : (
          <div style={{ width: '14mm', height: '14mm', borderRadius: '50%', background: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '10px', fontWeight: 700, border: '1.5px solid #fff' }}>
            {emp.first_name?.[0]}{emp.last_name?.[0]}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ position: 'absolute', top: '10mm', left: '20mm', right: '2mm' }}>
        <p style={{ fontSize: '9px', fontWeight: 700, color: '#1a1a1a', margin: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{emp.first_name} {emp.last_name}</p>
        {showDesignation && <p style={{ fontSize: '7.5px', color: '#555', margin: '1px 0 0', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{strVal(emp.designation) || '–'}</p>}
        {showDept && <p style={{ fontSize: '7px', color: '#888', margin: '1px 0 0', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{strVal(emp.department) || ''}</p>}
        <div style={{ marginTop: '2mm' }}>
          {showEmpCode && <p style={{ fontSize: '7px', color: '#555', margin: '1px 0 0' }}><strong>ID:</strong> {emp.employee_code}</p>}
          {showJoining && <p style={{ fontSize: '7px', color: '#555', margin: '1px 0 0' }}><strong>Joined:</strong> {emp.joining_date}</p>}
        </div>
      </div>

      {/* QR placeholder */}
      <div style={{ position: 'absolute', bottom: '4mm', right: '3mm', width: '10mm', height: '10mm', background: '#f0f0f0', borderRadius: '1mm', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="0" width="4" height="4" fill="#999"/><rect x="6" y="0" width="4" height="4" fill="#999"/>
          <rect x="0" y="6" width="4" height="4" fill="#999"/><rect x="5" y="5" width="2" height="2" fill="#999"/>
          <rect x="8" y="8" width="2" height="2" fill="#999"/>
        </svg>
      </div>

      {/* Bottom band */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '2mm', background: accentColor }}/>
    </div>
  );
}

function PersonalInfoSheet({ emp, companyName, settings }) {
  const {
    accentColor = '#5B6CF0',
    showIdentity = true, showEmployment = true, showContact = true,
    showBank = true, showConfidential = true,
  } = settings || {};
  const ext = emp.extended_details || {};

  const allSections = [
    { key: 'identity', title: 'Identity', show: showIdentity, rows: [
      ['Employee Code', emp.employee_code],
      ['Full Name', `${emp.first_name} ${emp.last_name}`],
      ['Date of Birth', ext.dob],
      ['Gender', ext.gender?.replace(/_/g, ' ')],
      ['Aadhaar', ext.aadhaar_number ? '••••' + ext.aadhaar_number.slice(-4) : null],
    ]},
    { key: 'employment', title: 'Employment', show: showEmployment, rows: [
      ['Designation', strVal(emp.designation) || null],
      ['Department', strVal(emp.department) || null],
      ['Employment Type', emp.employment_type?.replace(/_/g, ' ')],
      ['Joining Date', emp.joining_date],
      ['Manager', emp.reporting_manager?.name || emp.reporting_manager || null],
    ]},
    { key: 'contact', title: 'Contact', show: showContact, rows: [
      ['Phone', emp.phone],
      ['Email', emp.email],
    ]},
    { key: 'bank', title: 'Bank Details', show: showBank, rows: [
      ['Bank', emp.bank_name],
      ['Account Holder', emp.account_holder_name],
      ['Account No.', emp.account_number ? '••••' + emp.account_number.slice(-4) : null],
      ['IFSC', emp.ifsc_code],
    ]},
  ].filter(s => s.show);

  return (
    <div className="personal-sheet" style={{ width: '190mm', minHeight: '277mm', fontFamily: 'Arial, sans-serif', background: '#fff', padding: '10mm', boxSizing: 'border-box', pageBreakAfter: 'always' }}>
      {/* Header */}
      <div style={{ borderBottom: `2px solid ${accentColor}`, paddingBottom: '4mm', marginBottom: '6mm', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2mm', marginBottom: '1mm' }}>
            <div style={{ width: '6mm', height: '6mm', background: accentColor, borderRadius: '1mm', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#fff', fontSize: '8px', fontWeight: 700 }}>{companyName?.[0]}</span>
            </div>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#1a1a1a', margin: 0 }}>{companyName}</p>
          </div>
          <p style={{ fontSize: '9px', color: '#888', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>Employee Personal Record</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: '8px', color: '#aaa', margin: 0 }}>Generated: {new Date().toLocaleDateString('en-IN')}</p>
          {showConfidential && <p style={{ fontSize: '8px', color: accentColor, fontWeight: 700, margin: '1mm 0 0', textTransform: 'uppercase', letterSpacing: '1px' }}>Confidential</p>}
        </div>
      </div>

      {/* Employee summary row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4mm', marginBottom: '6mm', padding: '3mm', background: '#f8f9fa', borderRadius: '2mm' }}>
        {emp.photo_url ? (
          <img src={emp.photo_url} alt="" style={{ width: '14mm', height: '14mm', borderRadius: '50%', objectFit: 'cover', border: '1px solid #ddd' }}/>
        ) : (
          <div style={{ width: '14mm', height: '14mm', borderRadius: '50%', background: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px', fontWeight: 700 }}>
            {emp.first_name?.[0]}{emp.last_name?.[0]}
          </div>
        )}
        <div>
          <p style={{ fontSize: '13px', fontWeight: 700, color: '#1a1a1a', margin: 0 }}>{emp.first_name} {emp.last_name}</p>
          <p style={{ fontSize: '10px', color: '#666', margin: '1px 0 0' }}>{strVal(emp.designation)} · {strVal(emp.department)}</p>
          <p style={{ fontSize: '9px', color: '#999', margin: '1px 0 0' }}>{emp.employee_code}</p>
        </div>
      </div>

      {/* Sections grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5mm', marginBottom: '6mm' }}>
        {allSections.map(sec => (
          <div key={sec.key}>
            <p style={{ fontSize: '8px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#aaa', borderBottom: '1px solid #e5e5e5', paddingBottom: '1.5mm', marginBottom: '2mm' }}>{sec.title}</p>
            {sec.rows.filter(([, v]) => v).map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', padding: '1mm 0', borderBottom: '0.5px solid #f5f5f5' }}>
                <span style={{ color: '#888' }}>{label}</span>
                <span style={{ fontWeight: 600, color: '#333', maxWidth: '50%', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(value)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Signature row */}
      <div style={{ borderTop: '1px solid #e5e5e5', paddingTop: '4mm', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4mm', textAlign: 'center' }}>
        {['Employee Signature', 'HR Manager', 'Authorized Signatory'].map(l => (
          <div key={l}>
            <div style={{ height: '8mm', borderBottom: '1px solid #bbb', marginBottom: '1.5mm' }}/>
            <p style={{ fontSize: '8px', color: '#aaa', margin: 0 }}>{l}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function IdCardsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const companyName = useSelector(selectCompanyName);
  const { data: empResp } = useEmployees({ limit: 500 });
  const allEmployees = useMemo(() => (empResp?.data || []).filter(e => e.status !== 'TERMINATED'), [empResp]);

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mode, setMode] = useState('id_card');
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  // Pre-select employees passed from the Employee page via router state
  useEffect(() => {
    const preselected = location.state?.preselected;
    if (preselected?.length && allEmployees.length > 0) {
      const validIds = new Set(preselected.filter(id => allEmployees.some(e => e.id === id)));
      if (validIds.size > 0) setSelectedIds(validIds);
    }
  // Only run once after employees load
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEmployees.length]);

  const setSetting = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return allEmployees.filter(e =>
      !q || `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
      (e.employee_code||'').toLowerCase().includes(q) ||
      (e.department?.name||'').toLowerCase().includes(q)
    );
  }, [allEmployees, search]);

  const selected = allEmployees.filter(e => selectedIds.has(e.id));
  const allFiltered = filtered.length > 0 && filtered.every(e => selectedIds.has(e.id));

  const toggleAll = () => setSelectedIds(prev => {
    const next = new Set(prev);
    allFiltered ? filtered.forEach(e => next.delete(e.id)) : filtered.forEach(e => next.add(e.id));
    return next;
  });
  const toggleOne = (id) => setSelectedIds(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  const handlePrint = () => {
    if (selected.length === 0 || allEmployees.length === 0) return;
    try { window.print(); } catch { /* ignore */ }
  };

  return (
    <div className="space-y-5 fade-in">

      {/* ── PRINT-ONLY CONTENT ─────────────────────────────────────────────── */}
      {/* This div is invisible on screen (visibility:hidden + zero height)
          but becomes fully visible during print via the @media print rule below.
          Using visibility instead of display so the React tree stays mounted. */}
      <div id="ax-print" aria-hidden="true">
        {selected.length > 0 && allEmployees.length > 0 && (
          mode === 'id_card' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 85.6mm)', gap: '5mm', padding: '10mm' }}>
              {selected.map(e => <IdCard key={e.id} emp={e} companyName={companyName} settings={settings}/>)}
            </div>
          ) : (
            <div>
              {selected.map(e => <PersonalInfoSheet key={e.id} emp={e} companyName={companyName} settings={settings}/>)}
            </div>
          )
        )}
      </div>

      {/* ── SCREEN UI ──────────────────────────────────────────────────────── */}
      <div id="ax-screen">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => navigate('/employees')} className="p-2 hover:bg-[var(--bg-muted)] rounded-[8px] transition-colors">
            <ArrowLeft size={18} className="text-[var(--text-secondary)]"/>
          </button>
          <div className="flex-1">
            <h1 className="text-[20px] font-bold text-[var(--text-primary)]">ID Cards & Personal Info</h1>
            <p className="text-[13px] text-[var(--text-secondary)]">Select employees to print ID cards or personal information sheets</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" icon={Settings2} onClick={() => setShowSettings(s => !s)}>Customize</Button>
            <Button size="sm" icon={Printer} onClick={handlePrint} disabled={selected.length === 0}>
              Print{selected.length > 0 ? ` (${selected.length})` : ''}
            </Button>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-2 mb-4">
          {[
            { key: 'id_card', icon: CreditCard, label: 'ID Card (85×54mm)' },
            { key: 'personal_info', icon: FileText, label: 'Personal Info Sheet (A4)' },
          ].map(({ key, icon: Icon, label }) => (
            <button key={key} onClick={() => setMode(key)}
              className={cn('flex items-center gap-2 px-4 py-2 rounded-[10px] text-[13px] font-semibold border transition-colors',
                mode === key ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--accent)]' : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]')}>
              <Icon size={14}/> {label}
            </button>
          ))}
        </div>

        {/* Settings panel — shown for both modes */}
        {showSettings && (
          <div className="card p-4 space-y-4 mb-4">
            <p className="text-[12px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">
              {mode === 'id_card' ? 'Card Settings' : 'Sheet Settings'}
            </p>

            {/* Accent color — shared */}
            <div>
              <p className="text-[12px] text-[var(--text-secondary)] mb-1.5">Accent Color</p>
              <div className="flex gap-2">
                {ACCENT_PRESETS.map(c => (
                  <button key={c} onClick={() => setSetting('accentColor', c)}
                    className={cn('w-6 h-6 rounded-full border-2 transition-all', settings.accentColor === c ? 'border-[var(--text-primary)] scale-110' : 'border-transparent')}
                    style={{ background: c }}/>
                ))}
              </div>
            </div>

            {/* ID Card fields */}
            {mode === 'id_card' && (
              <div>
                <p className="text-[12px] text-[var(--text-secondary)] mb-1.5">Show Fields</p>
                <div className="flex flex-wrap gap-3">
                  {[['showDesignation','Designation'],['showDept','Department'],['showEmpCode','Employee Code'],['showJoining','Joining Date']].map(([k, l]) => (
                    <label key={k} className="flex items-center gap-1.5 text-[12px] text-[var(--text-secondary)] cursor-pointer">
                      <input type="checkbox" checked={!!settings[k]} onChange={e => setSetting(k, e.target.checked)} className="accent-[var(--accent)]"/>
                      {l}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Personal Info sections */}
            {mode === 'personal_info' && (
              <div className="space-y-3">
                <div>
                  <p className="text-[12px] text-[var(--text-secondary)] mb-1.5">Show Sections</p>
                  <div className="flex flex-wrap gap-3">
                    {[['showIdentity','Identity'],['showEmployment','Employment'],['showContact','Contact'],['showBank','Bank Details']].map(([k, l]) => (
                      <label key={k} className="flex items-center gap-1.5 text-[12px] text-[var(--text-secondary)] cursor-pointer">
                        <input type="checkbox" checked={!!settings[k]} onChange={e => setSetting(k, e.target.checked)} className="accent-[var(--accent)]"/>
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-1.5 text-[12px] text-[var(--text-secondary)] cursor-pointer">
                  <input type="checkbox" checked={!!settings.showConfidential} onChange={e => setSetting('showConfidential', e.target.checked)} className="accent-[var(--accent)]"/>
                  Show "Confidential" stamp in header
                </label>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Left: employee selector */}
          <div className="card p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-2">
              <button onClick={() => allEmployees.length > 0 && toggleAll()} disabled={allEmployees.length === 0} className="shrink-0 text-[var(--text-tertiary)] hover:text-[var(--accent)] disabled:opacity-30">
                {allFiltered ? <CheckSquare size={15} className="text-[var(--accent)]"/> : <Square size={15}/>}
              </button>
              <div className="relative flex-1">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none"/>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employees…"
                  className="w-full h-8 pl-8 pr-3 bg-[var(--bg-muted)] rounded-[8px] text-[13px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"/>
              </div>
              <span className="text-[11px] text-[var(--text-tertiary)] shrink-0">{selected.length} selected</span>
            </div>
            <div className="divide-y divide-[var(--border)] max-h-[500px] overflow-y-auto">
              {filtered.map(e => (
                <div key={e.id}
                  className={cn('flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-[var(--bg-muted)] transition-colors', selectedIds.has(e.id) && 'bg-[var(--accent)]/5')}
                  onClick={() => toggleOne(e.id)}>
                  <button type="button" className="shrink-0 text-[var(--text-tertiary)]" onClick={ev => { ev.stopPropagation(); toggleOne(e.id); }}>
                    {selectedIds.has(e.id) ? <CheckSquare size={14} className="text-[var(--accent)]"/> : <Square size={14}/>}
                  </button>
                  <Avatar name={`${e.first_name} ${e.last_name}`} src={e.photo_url} size="sm"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-[var(--text-primary)]">{e.first_name} {e.last_name}</p>
                    <p className="text-[11px] text-[var(--text-tertiary)]">{e.employee_code} · {strVal(e.designation) || strVal(e.department) || ''}</p>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && <p className="px-4 py-8 text-center text-[14px] text-[var(--text-tertiary)]">No employees found</p>}
            </div>
          </div>

          {/* Right: live preview */}
          <div className="space-y-3">
            <p className="text-[12px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide">
              Preview {selected.length > 0 ? `(${selected.length} selected)` : ''}
            </p>
            {selected.length === 0 ? (
              <div className="card py-16 text-center text-[14px] text-[var(--text-tertiary)]">
                Select employees on the left to preview
              </div>
            ) : mode === 'id_card' ? (
              <div className="flex flex-wrap gap-3 max-h-[580px] overflow-y-auto pr-1">
                {selected.map(e => <IdCard key={e.id} emp={e} companyName={companyName} settings={settings}/>)}
              </div>
            ) : (
              <div className="space-y-4 max-h-[580px] overflow-y-auto pr-1">
                {selected.map(e => (
                  <div key={e.id} className="rounded-[10px] border border-[var(--border)] overflow-hidden bg-white"
                    style={{ transform: 'scale(0.62)', transformOrigin: 'top left', width: '161%', marginBottom: '-38%' }}>
                    <PersonalInfoSheet emp={e} companyName={companyName} settings={settings}/>
                  </div>
                ))}
              </div>
            )}
            {selected.length > 0 && (
              <Button icon={Printer} onClick={handlePrint} className="w-full mt-2">
                Print / Save as PDF — {selected.length} {mode === 'id_card' ? `card${selected.length !== 1 ? 's' : ''}` : `sheet${selected.length !== 1 ? 's' : ''}`}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/*
        Print CSS — "visibility" trick:
        - `body * { visibility: hidden }` hides everything (including #root)
          but unlike display:none, children can OVERRIDE with visibility:visible
        - Only #ax-print and its children are made visible
        - #ax-print is positioned fixed at top-left so it fills the page
        - #ax-screen is invisible (inherits hidden, no override)
      */}
      <style>{`
        #ax-print { visibility: hidden; height: 0; overflow: hidden; }
        @media print {
          body * { visibility: hidden !important; }
          #ax-print { visibility: visible !important; position: fixed; inset: 0; height: auto; overflow: visible; z-index: 9999; background: white; }
          #ax-print * { visibility: visible !important; }
          #ax-screen { display: none !important; }
          .personal-sheet { page-break-after: always; }
          @page { margin: 5mm; size: A4; }
        }
      `}</style>
    </div>
  );
}
